import { useState, useEffect } from 'react';
import { Formik, Form, yupToFormErrors } from 'formik';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';

import Yup from '../../../utils/yup';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import useMenus from '../../../context/MenuContext';
import useUsers from '../../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../../Interface';
import AlertFormReports from './AlertFormReports';
import AlertFormFields from './AlertFormFields';
import { cleanValues, generateAutocompleteItems } from '../../../utils/helper';
import masterDataService from '../../../service/masterData.service';
import alertFormService from '../../../service/Alert/alertForm.service';
import { useLoader } from '../../../provider/LoaderProvider';
import AlertFormTabs from './AlertFormTabs';
import { AlertTypeEnum, AlertUnitEnum, WeekDaysList } from '../../../config/alert';
import { DashboardPdfExportModeEnum } from '../../../config/dashboard';
import AlertFormDialog from './AlertFormDialog';
import dashboardService from '../../../service/dashboard.service';

const getScheduleTimeKey = (time) => (time ? dayjs(time).format('HH:mm') : '');

const serializeScheduleTime = (time) => {
  if (!time) return null;

  return dayjs(time)
    .year(1970)
    .month(0)
    .date(1)
    .second(0)
    .millisecond(0)
    .toISOString();
};

const getUniqueScheduleTimes = (schedules) =>
  Array.from(
    new Map(
      schedules
        .filter((schedule) => schedule.time)
        .map((schedule) => [getScheduleTimeKey(schedule.time), schedule.time])
    ).values()
  ).sort((firstTime, secondTime) => getScheduleTimeKey(firstTime).localeCompare(getScheduleTimeKey(secondTime)));

const AlertFormPage = () => {
  const { showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    views: [],
    procedureViews: [],
    dashboards: [],
  });
  const [autocompleteData, setAutoCompleteData] = useState({
    user: [],
    roles: [],
    views: [],
    dashboards: [],
  });
  const [submenu, setSubmenu] = useState(null);

  const [alertFormDialogOpen, setAlertFormDialogOpen] = useState(false);

  const defSchedule = {
    id: null,
    unit: AlertUnitEnum.MINUTE,
    every: 1,
    time: null,
    weekday: null,
    monthDay: null,
    customWeekdays: [],
    customWeekTimes: [],
    loadedSchedules: [],
  };

  const def = {
    id: null,
    name: '',
    active: true,
    type: AlertTypeEnum.ALERT,
    view: '',
    dashboardId: null,
    dashboard: null,
    pdfExportMode: DashboardPdfExportModeEnum.FULL,
    description: '',
    alertRecipient: [],
    alertEmailRecipient: [],
    alertSchedule: defSchedule,
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        dashboardService
          .allDashboards()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        alertFormService
          .getViews()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .rolesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    showLoader();

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          views: resp[2],
          dashboards: resp[1],
          roles: resp[3],
        };

        setAutoCompleteData(aData);

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          views: generateAutocompleteItems(aData.views, 'TABLE_NAME', 'TABLE_NAME'),
          procedureViews: generateAutocompleteItems(
            aData.views.filter((view) => view.IS_PROC === true),
            'TABLE_NAME',
            'TABLE_NAME'
          ),
          dashboards: generateAutocompleteItems(aData.dashboards, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);

    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('alertForm');
    setSubmenu(submenuId);
    setCanEdit(true); //getCanEdit(submenuId);
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const recipientSchema = Yup.object()
    .shape({
      userId: Yup.number().nullable(),
      groupId: Yup.number().nullable(),
    })
    .test(
      'exactly-one-recipient-type',
      'Pontosan egy felhasználót vagy szerepkört kell kiválasztani.',
      (recipient) => (recipient?.userId != null) !== (recipient?.groupId != null)
    );

  const uniqueRecipients = (recipients = []) => {
    const recipientKeys = recipients.map((recipient) =>
      recipient.userId != null ? `user-${recipient.userId}` : `group-${recipient.groupId}`
    );
    return new Set(recipientKeys).size === recipientKeys.length;
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    type: Yup.string().oneOf(Object.values(AlertTypeEnum)).required(),
    view: Yup.string()
      .nullable()
      .test('relevant-source-required', 'A forrás kiválasztása kötelező.', function (value) {
        return this.parent.type === AlertTypeEnum.DASHBOARD_EMAIL || Boolean(value);
      })
      .test('scheduled-task-procedure', 'Ütemezett feladatnál csak tárolt eljárás választható.', function (value) {
        if (this.parent.type !== AlertTypeEnum.SCHEDULED_TASK || !value) return true;
        return autocompleteData.views.some((view) => view.TABLE_NAME === value && view.IS_PROC === true);
      }),
    dashboardId: Yup.number()
      .nullable()
      .test('dashboard-email-dashboard', 'Dashboard küldésénél a dashboard kiválasztása kötelező.', function (value) {
        return this.parent.type !== AlertTypeEnum.DASHBOARD_EMAIL || value != null;
      }),
    pdfExportMode: Yup.string()
      .nullable()
      .oneOf(Object.values(DashboardPdfExportModeEnum))
      .test('dashboard-email-pdf-export-mode', 'A PDF riport típusának kiválasztása kötelező.', function (value) {
        return this.parent.type !== AlertTypeEnum.DASHBOARD_EMAIL || value != null;
      }),
    active: Yup.boolean(),
    description: Yup.string().nullable(),
    alertRecipient: Yup.array()
      .of(recipientSchema)
      .test('unique-alert-recipient', 'Ugyanaz a címzett csak egyszer választható ki.', uniqueRecipients)
      .test('alert-recipient-type', 'Ennél a típusnál nem adható meg belső címzett.', function (recipients) {
        return this.parent.type === AlertTypeEnum.ALERT || (recipients?.length ?? 0) === 0;
      }),
    alertEmailRecipient: Yup.array()
      .of(recipientSchema)
      .test(
        'dashboard-email-recipient',
        'Dashboard küldésénél legalább egy e-mail-címzett kiválasztása kötelező.',
        function (recipients) {
          return this.parent.type !== AlertTypeEnum.DASHBOARD_EMAIL || (recipients?.length ?? 0) > 0;
        }
      )
      .test('scheduled-task-email-recipient', 'Ütemezett feladatnál nem adható meg e-mail-címzett.', function (recipients) {
        return this.parent.type !== AlertTypeEnum.SCHEDULED_TASK || (recipients?.length ?? 0) === 0;
      })
      .test('unique-email-recipient', 'Ugyanaz a címzett csak egyszer választható ki.', uniqueRecipients),
    alertSchedule: Yup.object().shape({
      unit: Yup.string().required(),
      every: Yup.number().required(),
      time: Yup.string()
        .nullable()
        .when('unit', (unit, schema) =>
          unit === AlertUnitEnum.DAY || unit === AlertUnitEnum.WEEK || unit === AlertUnitEnum.MONTH
            ? schema.required()
            : schema
        ),
      weekday: Yup.number()
        .nullable()
        .when('unit', (unit, schema) => (unit === AlertUnitEnum.WEEK ? schema.required() : schema)),
      monthDay: Yup.number()
        .nullable()
        .when('unit', (unit, schema) => (unit === AlertUnitEnum.MONTH ? schema.required() : schema)),
      customWeekdays: Yup.array()
        .of(Yup.number().oneOf(WeekDaysList.map((day) => day.value)))
        .when('unit', (unit, schema) =>
          unit === AlertUnitEnum.CUSTOM_WEEK
            ? schema
                .min(1, 'Legalább egy nap kiválasztása kötelező.')
                .required('Legalább egy nap kiválasztása kötelező.')
            : schema
        ),
      customWeekTimes: Yup.array()
        .of(Yup.mixed().nullable().required('Az időpont megadása kötelező.'))
        .when('unit', (unit, schema) =>
          unit === AlertUnitEnum.CUSTOM_WEEK
            ? schema
                .min(1, 'Legalább egy időpont megadása kötelező.')
                .required('Legalább egy időpont megadása kötelező.')
                .test('unique-schedule-times', 'Ugyanaz az időpont csak egyszer adható meg.', (times = []) => {
                  const timeKeys = times.filter(Boolean).map(getScheduleTimeKey);
                  return new Set(timeKeys).size === timeKeys.length;
                })
            : schema
        ),
    }),
  });

  const valuesToForm = () => {
    const {
      id,
      name,
      active,
      type,
      view,
      dashboardId,
      dashboard,
      pdfExportMode,
      description,
      alertRecipient,
      alertEmailRecipient,
      alertSchedule,
    } = selectedValues;
    const alertType = type ?? AlertTypeEnum.ALERT;
    const alertSchedules = Array.isArray(alertSchedule) ? alertSchedule : alertSchedule ? [alertSchedule] : [];
    const primarySchedule = alertSchedules[0] ?? defSchedule;
    const customWeekSchedules = alertSchedules.filter(
      (schedule) => schedule.unit === AlertUnitEnum.CUSTOM_WEEK
    );

    setInitValues({
      id,
      name,
      active,
      type: alertType,
      view: alertType === AlertTypeEnum.DASHBOARD_EMAIL ? null : view,
      dashboardId: alertType === AlertTypeEnum.DASHBOARD_EMAIL ? dashboardId ?? null : null,
      dashboard: alertType === AlertTypeEnum.DASHBOARD_EMAIL ? dashboard ?? null : null,
      pdfExportMode:
        alertType === AlertTypeEnum.DASHBOARD_EMAIL
          ? pdfExportMode ?? DashboardPdfExportModeEnum.FULL
          : null,
      description,
      alertRecipient:
        alertType !== AlertTypeEnum.ALERT
          ? []
          : (alertRecipient ?? []).map((ar) => ({
              ...(ar.id != null ? { id: ar.id } : {}),
              userId: ar.userId ?? null,
              groupId: ar.groupId ?? null,
            })),
      alertEmailRecipient:
        alertType === AlertTypeEnum.SCHEDULED_TASK
          ? []
          : (alertEmailRecipient ?? []).map((recipient) => ({
              ...(recipient.id != null ? { id: recipient.id } : {}),
              userId: recipient.userId ?? null,
              groupId: recipient.groupId ?? null,
            })),
      alertSchedule: {
        id: primarySchedule.id ?? null,
        unit: primarySchedule.unit ?? AlertUnitEnum.MINUTE,
        every: primarySchedule.every ?? 1,
        time: primarySchedule.time ?? null,
        weekday: primarySchedule.weekday ?? null,
        monthDay: primarySchedule.monthDay ?? null,
        customWeekdays: [
          ...new Set(customWeekSchedules.map((schedule) => schedule.weekday).filter((day) => day != null)),
        ].sort((firstDay, secondDay) => firstDay - secondDay),
        customWeekTimes: getUniqueScheduleTimes(customWeekSchedules),
        loadedSchedules: alertSchedules,
      },
    });
  };

  const valuesToPayload = (values) => {
    const type = values.type ?? AlertTypeEnum.ALERT;
    const schedule = values.alertSchedule;
    let alertSchedules;

    if (schedule.unit === AlertUnitEnum.CUSTOM_WEEK) {
      const weekdays = [...new Set(schedule.customWeekdays ?? [])].sort(
        (firstDay, secondDay) => firstDay - secondDay
      );
      const times = Array.from(
        new Map((schedule.customWeekTimes ?? []).map((time) => [getScheduleTimeKey(time), time])).values()
      ).sort((firstTime, secondTime) => getScheduleTimeKey(firstTime).localeCompare(getScheduleTimeKey(secondTime)));

      alertSchedules = weekdays.flatMap((weekday) =>
        times.map((time) => {
          const existingSchedule = (schedule.loadedSchedules ?? []).find(
            (record) =>
              record.unit === AlertUnitEnum.CUSTOM_WEEK &&
              Number(record.weekday) === Number(weekday) &&
              getScheduleTimeKey(record.time) === getScheduleTimeKey(time)
          );

          return {
            ...(existingSchedule?.id != null ? { id: existingSchedule.id } : {}),
            unit: schedule.unit,
            every: schedule.every,
            weekday,
            time: serializeScheduleTime(time),
            monthDay: null,
          };
        })
      );
    } else {
      const existingSchedule = (schedule.loadedSchedules ?? []).find(
        (record) => record.unit === schedule.unit
      );

      alertSchedules = [
        {
          ...(existingSchedule?.id != null ? { id: existingSchedule.id } : {}),
          unit: schedule.unit,
          every: schedule.every,
          time: serializeScheduleTime(schedule.time),
          weekday: schedule.unit === AlertUnitEnum.WEEK ? schedule.weekday : null,
          monthDay: schedule.unit === AlertUnitEnum.MONTH ? schedule.monthDay : null,
        },
      ];
    }

    return cleanValues({
      id: values.id,
      name: values.name,
      active: values.active,
      type,
      view: type === AlertTypeEnum.DASHBOARD_EMAIL ? null : values.view,
      dashboardId: type === AlertTypeEnum.DASHBOARD_EMAIL ? values.dashboardId : null,
      pdfExportMode: type === AlertTypeEnum.DASHBOARD_EMAIL ? values.pdfExportMode : null,
      description: values.description,
      alertRecipient:
        type !== AlertTypeEnum.ALERT
          ? []
          : (values.alertRecipient ?? []).map((recipient) => ({
              ...(recipient.id != null ? { id: recipient.id } : {}),
              userId: recipient.userId ?? null,
              groupId: recipient.groupId ?? null,
            })),
      alertEmailRecipient:
        type === AlertTypeEnum.SCHEDULED_TASK
          ? []
          : (values.alertEmailRecipient ?? []).map((recipient) => ({
              ...(recipient.id != null ? { id: recipient.id } : {}),
              userId: recipient.userId ?? null,
              groupId: recipient.groupId ?? null,
            })),
      alertSchedule: alertSchedules,
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = valuesToPayload(values);

    alertFormService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = valuesToPayload(values);

    alertFormService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    alertFormService.deleteData(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  const validate = async (values) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      return {};
    } catch (err) {
      const formErrors = yupToFormErrors(err);

      if (formErrors.alertRecipient || formErrors.alertEmailRecipient) {
        addNotification({
          content: 'Kérjük, ellenőrizze a riasztás címzettjeit!',
          type: NotificationType.WARNING,
        });
      }

      if (formErrors.alertSchedule) {
        addNotification({
          content: 'Kérjük, ellenőrizze a riasztás ütemezését!',
          type: NotificationType.WARNING,
        });
      }

      addNotification({
        content: 'Kérjük, javítsa a hibásan kitöltött mezőket!',
        type: NotificationType.WARNING,
      });

      // Itt is eléred a hibákat: formErrors
      return formErrors;
    }
  };

  const openAlertFormDialog = () => {
    setAlertFormDialogOpen(true);
  };

  const handleAlertFormDialogClose = () => {
    setAlertFormDialogOpen(false);
  };

  return (
    <>
      <FormBase
        name="AlertForm"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validateOnChange={false}
                validateOnBlur={false}
                validate={validate}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm, values }) => (
                  <Form>
                    <AlertFormFields
                      viewField={viewField}
                      autocompleteOptions={autocompleteOptions}
                      autocompleteData={autocompleteData}
                      openAlertFormDialog={openAlertFormDialog}
                    />

                    <AlertFormTabs
                      submenuId={submenu}
                      selectedValues={selectedValues}
                      setDefaultValues={setDefaultValues}
                      viewField={viewField}
                      autocompleteOptions={autocompleteOptions}
                      autocompleteData={autocompleteData}
                      disabled={viewField}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                    <AlertFormDialog
                      open={alertFormDialogOpen}
                      onClose={handleAlertFormDialogClose}
                      autocompleteData={autocompleteData}
                      name={values.name}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <AlertFormReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autocompleteOptions={autocompleteOptions}
            autocompleteData={autocompleteData}
            submenuId={submenu}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default AlertFormPage;
